// src/react/ColumnPlugin.tsx
import { toPlatePlugin } from "@udecode/plate/react";

// src/lib/BaseColumnPlugin.ts
import { createSlatePlugin } from "@udecode/plate";

// src/lib/withColumn.ts
import { ElementApi } from "@udecode/plate";
var withColumn = ({
  editor,
  tf: { deleteBackward, normalizeNode }
}) => ({
  transforms: {
    deleteBackward(unit) {
      if (editor.api.isCollapsed()) {
        const entry = editor.api.above({
          match: (n) => ElementApi.isElement(n) && n.type === BaseColumnItemPlugin.key
        });
        if (entry) {
          const [node, path] = entry;
          if (node.children.length > 1) return deleteBackward(unit);
          const isStart = editor.api.isStart(editor.selection?.anchor, path);
          if (isStart) return;
        }
      }
      deleteBackward(unit);
    },
    normalizeNode([n, path]) {
      if (ElementApi.isElement(n) && n.type === BaseColumnPlugin.key) {
        const node = n;
        if (!node.children.some(
          (child) => ElementApi.isElement(child) && child.type === editor.getType(BaseColumnItemPlugin)
        )) {
          editor.tf.removeNodes({ at: path });
          return;
        }
        if (node.children.length < 2) {
          editor.tf.withoutNormalizing(() => {
            editor.tf.unwrapNodes({ at: path });
            editor.tf.unwrapNodes({ at: path });
          });
          return;
        }
        editor.tf.withoutNormalizing(() => {
          const totalColumns = node.children.length;
          let widths = node.children.map((col) => {
            const parsed = Number.parseFloat(col.width);
            return Number.isNaN(parsed) ? 0 : parsed;
          });
          const sum = widths.reduce((acc, w) => acc + w, 0);
          if (sum !== 100) {
            const diff = 100 - sum;
            const adjustment = diff / totalColumns;
            widths = widths.map((w) => w + adjustment);
            widths.forEach((w, i) => {
              const columnPath = path.concat([i]);
              editor.tf.setNodes(
                { width: `${w}%` },
                { at: columnPath }
              );
            });
          }
        });
      }
      if (ElementApi.isElement(n) && n.type === BaseColumnItemPlugin.key) {
        const node = n;
        if (node.children.length === 0) {
          editor.tf.removeNodes({ at: path });
          return;
        }
      }
      return normalizeNode([n, path]);
    }
  }
});

// src/lib/BaseColumnPlugin.ts
var BaseColumnItemPlugin = createSlatePlugin({
  key: "column",
  node: { isElement: true }
}).overrideEditor(withColumn);
var BaseColumnPlugin = createSlatePlugin({
  key: "column_group",
  node: { isElement: true },
  plugins: [BaseColumnItemPlugin]
});

// src/react/onKeyDownColumn.ts
import { isHotkey, PathApi } from "@udecode/plate";
var onKeyDownColumn = ({ editor, event }) => {
  if (event.defaultPrevented) return;
  const at = editor.selection;
  if (isHotkey("mod+a", event) && at) {
    const aboveNode = editor.api.above();
    const ancestorNode = editor.api.block({ highest: true });
    if (!ancestorNode) return;
    if (!aboveNode) return;
    const [node] = ancestorNode;
    if (node.type !== ColumnPlugin.key) return;
    const [, abovePath] = aboveNode;
    let targetPath = PathApi.parent(abovePath);
    if (editor.api.isAt({ block: true, end: true, start: true })) {
      targetPath = PathApi.parent(targetPath);
    }
    if (targetPath.length === 0) return;
    editor.tf.select(targetPath);
    event.preventDefault();
    event.stopPropagation();
  }
};

// src/react/ColumnPlugin.tsx
var ColumnItemPlugin = toPlatePlugin(BaseColumnItemPlugin);
var ColumnPlugin = toPlatePlugin(BaseColumnPlugin, {
  handlers: {
    onKeyDown: onKeyDownColumn
  },
  plugins: [ColumnItemPlugin]
});

// src/react/hooks/useDebouncePopoverOpen.ts
import {
  useEditorSelector,
  useReadOnly,
  useSelected
} from "@udecode/plate/react";
var useDebouncePopoverOpen = () => {
  const readOnly = useReadOnly();
  const selected = useSelected();
  const selectionCollapsed = useEditorSelector(
    (editor) => editor.api.isCollapsed(),
    []
  );
  return !readOnly && selected && selectionCollapsed;
};
export {
  ColumnItemPlugin,
  ColumnPlugin,
  onKeyDownColumn,
  useDebouncePopoverOpen
};
//# sourceMappingURL=index.mjs.map